import React from "react";
import moment from "moment";
import userImage from "../../../files/user.png";

const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatName = (name) => {
  if (!name || typeof name !== "string") return "-";
  return name.split(",").map(part => capitalizeFirstLetter(part.trim())).join(", ");
};

const CPT_CODE_99458 = "99458";
const CPT_CODE_99470 = "99470";
const CPT_CODE_99457 = "99457";
const CPT_CODE_99490 = "99490";
const CPT_CODE_99439 = "99439";
const CPT_CODE_99445 = "99445";
const CPT_CODE_99454 = "99454";
const RPM_CLINICAL_CODES = new Set([CPT_CODE_99470, CPT_CODE_99457, CPT_CODE_99458]);
const CCM_CLINICAL_CODES = new Set([CPT_CODE_99490, CPT_CODE_99439]);
const TECHNICAL_CODES = new Set([CPT_CODE_99445, CPT_CODE_99454]);
/** Same button classes as PM Session Report billable CPT codes (CCMList). */
const RPM_CLINICAL_PM_BADGE_CLASS = {
  [CPT_CODE_99457]: "btn btn-sm btn-info text-white session-code-button-radial rpm-billable-pm-code-badge",
  [CPT_CODE_99470]: "btn btn-sm btn-success text-white session-code-button-radial cptCodeButton99470 rpm-billable-pm-code-badge",
  [CPT_CODE_99458]: "btn btn-sm btn-warning session-code-button-radial rpm-billable-pm-code-badge"
};
const CCM_CLINICAL_PM_BADGE_CLASS = {
  [CPT_CODE_99490]: "btn btn-sm btn-info text-white session-code-button-radial rpm-billable-pm-code-badge",
  [CPT_CODE_99439]: "btn btn-sm btn-warning session-code-button-radial rpm-billable-pm-code-badge"
};
const TECHNICAL_PM_BADGE_CLASS = {
  [CPT_CODE_99445]: "btn btn-sm btn-success text-white session-code-button-radial cptCodeButton99445 rpm-billable-pm-code-badge",
  [CPT_CODE_99454]: "btn btn-sm btn-success text-white session-code-button-radial rpm-billable-pm-code-badge"
};
const MAX_99458_DISPLAY_COUNT = 3;
const MAX_99439_DISPLAY_COUNT = 2;
const DEFAULT_CPT_TIME = "00:00:00";
const PM_SESSION_ZERO_TIME = "0:00:00";
export const CPT_DETAILS_LAYOUT_RPM_CLINICAL = "rpmClinical";
export const CPT_DETAILS_LAYOUT_CCM_CLINICAL = "ccmClinical";
export const CPT_DETAILS_LAYOUT_TECHNICAL = "technical";

export const isPmServiceDetailsLayout = (layout) =>
  layout === CPT_DETAILS_LAYOUT_RPM_CLINICAL ||
  layout === CPT_DETAILS_LAYOUT_CCM_CLINICAL ||
  layout === CPT_DETAILS_LAYOUT_TECHNICAL;

/** @deprecated Use isPmServiceDetailsLayout */
export const isPmClinicalDetailsLayout = isPmServiceDetailsLayout;

/** Parse H:MM:SS, MM:SS, or plain minutes into total minutes. */
export const parseTimeToMinutes = (timeStr) => {
  if (timeStr === null || timeStr === undefined || timeStr === "") return 0;
  const raw = String(timeStr).trim();
  if (!raw) return 0;

  const numericOnly = Number(raw);
  if (!Number.isNaN(numericOnly) && !raw.includes(":")) {
    return numericOnly;
  }

  const parts = raw.split(":").map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) return 0;

  if (parts.length === 3) {
    return parts[0] * 60 + parts[1] + parts[2] / 60;
  }
  if (parts.length === 2) {
    return parts[0] + parts[1] / 60;
  }
  return 0;
};

/**
 * 99458 billing display: >= 40 min => 1 code, then +1 per additional 20 min (max 3/month).
 * e.g. 40m => 1, 60m => 2, 80m => 3
 */
export const get99458DisplayCount = (totalMinutes) => {
  const minutes = Number(totalMinutes) || 0;
  if (minutes < 40) return 1;
  return Math.min(MAX_99458_DISPLAY_COUNT, 1 + Math.floor((minutes - 40) / 20));
};

/**
 * 99439 billing display: >= 40 min CCM => 1 code, then +1 per additional 20 min (max 2/month).
 */
export const get99439DisplayCount = (totalMinutes) => {
  const minutes = Number(totalMinutes) || 0;
  if (minutes < 40) return 0;
  return Math.min(MAX_99439_DISPLAY_COUNT, 1 + Math.floor((minutes - 40) / 20));
};

const normalizeCptCodeKey = (code) => String(code ?? "").trim();

const get99458GroupState = (rows99458 = []) => {
  if (rows99458.length === 0) return null;

  const totalMinutes = rows99458.reduce((sum, row) => sum + parseTimeToMinutes(row.time), 0);
  return {
    template: rows99458[0],
    displayCount: get99458DisplayCount(totalMinutes)
  };
};

const renderCpt99458Group = ({ template, displayCount, showStatus = false }) => {
  const statusClass =
    template.status === "not met" || template.status === "notmet"
      ? "not-met"
      : template.isMet
        ? "met"
        : "";

  return (
    <div className="cpt-code-item cpt-code-item-99458-group" key="cpt-99458-group">
      {showStatus && template.status && (
        <span className={`patient-status-badge ${statusClass}`}>
          {template.status.toUpperCase()}
        </span>
      )}
      <div className="cpt-code-badges-wrap">
        {Array.from({ length: displayCount }, (_, index) => (
          <span
            key={`99458-badge-${index}`}
            className={`cpt-code-badge ${template.isMet ? "met" : "not-met"}`}
          >
            {CPT_CODE_99458}
          </span>
        ))}
      </div>
      <span className="cpt-code-name">{template.name || CPT_CODE_99458}</span>
      <CptMetricDisplay details={template} />
    </div>
  );
};

export const toCapitalizedEmail = (value) => {
  if (!value || typeof value !== "string") {
    return "-";
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getOrganizationIcon = (organization) => {
  if (!organization) return <i className="mdi mdi-hospital-building"></i>;
  const orgLower = organization.toLowerCase();
  if (orgLower.includes("hospital") || orgLower.includes("medical")) {
    return <i className="mdi mdi-hospital-building"></i>;
  }
  if (orgLower.includes("clinic")) {
    return <i className="mdi mdi-medical-clinic"></i>;
  }
  if (orgLower.includes("health") || orgLower.includes("wellness")) {
    return <i className="mdi mdi-heart-pulse"></i>;
  }
  if (orgLower.includes("care")) {
    return <i className="mdi mdi-hand-heart"></i>;
  }
  if (orgLower.includes("senior") || orgLower.includes("elder")) {
    return <i className="mdi mdi-account-heart"></i>;
  }
  return <i className="mdi mdi-hospital-building"></i>;
};

export const PatientNameCell = ({ name, email, phone, profileImage, isActive, onViewPatient }) => {
  const formattedName = formatName(name);
  return (
    <div className="patient-profile-cell">
      <img src={profileImage || userImage} alt={formattedName || "Patient"} className="patient-profile-avatar" />
      <div className="patient-profile-content">
        <div className="patient-profile-name-row">
          {onViewPatient ? (
            <button type="button" className="patient-profile-name-link" onClick={onViewPatient}>
              {formattedName}
            </button>
          ) : (
            <div className="patient-profile-name">{formattedName}</div>
          )}
          <span className={`patient-active-tag ${isActive ? "active" : "inactive"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
        <div className="patient-profile-meta">
          <i className="mdi mdi-email-outline"></i>
          <span>{toCapitalizedEmail(email)}</span>
        </div>
        <div className="patient-profile-meta">
          <i className="mdi mdi-phone-outline"></i>
          <span>{phone || "-"}</span>
        </div>
      </div>
    </div>
  );
};

export const DateCell = ({ date, iconClass = "mdi-calendar-outline" }) => (
  <div className="date-cell-wrapper">
    <i className={`mdi ${iconClass} date-icon`}></i>
    <span className="date-text">{date || "-"}</span>
  </div>
);

export const OrganizationCell = ({ organization }) => {
  const formattedOrg = organization === "-" ? "-" : capitalizeFirstLetter(organization);
  const orgIcon = getOrganizationIcon(organization);
  return (
    <div className="org-cell-wrapper">
      <span className="org-icon">{orgIcon}</span>
      <span className="org-text">{formattedOrg}</span>
    </div>
  );
};

export const ProgramCell = ({ programs }) => (
  <span className="patient-program-tag">
    {programs && programs !== "-" ? String(programs).toUpperCase() : "-"}
  </span>
);

export const AdherenceDaysCell = ({ days }) => (
  <span className="patient-number-badge">{days || 0}</span>
);

export const InsuranceCell = ({ insurance }) => (
  <span className="patient-table-text-wrap">{insurance === "-" ? "-" : capitalizeFirstLetter(insurance)}</span>
);

const matchesCptStatusFilter = (isMet, cptStatusFilter) => {
  if (cptStatusFilter === "met") return isMet;
  if (cptStatusFilter === "notMet") return !isMet;
  return true;
};

const normalizeDetailMap = (obj = {}) => Object.entries(obj).reduce((acc, [key, value]) => {
  const normalizedKey = String(key).toLowerCase().replace(/[\s_-]/g, "");
  acc[normalizedKey] = value;
  return acc;
}, {});

const pickDetailValue = (details = {}, ...keys) => {
  const entry = normalizeDetailMap(details);
  for (const key of keys) {
    const value = entry[key.toLowerCase().replace(/[\s_-]/g, "")];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const formatDisplayTime = (time) => {
  if (time === null || time === undefined || time === "") return null;

  const raw = String(time).trim();
  if (!raw || raw === "0:00:00" || raw === "00:00:00") return null;

  if (/^\d+$/.test(raw)) {
    const totalSeconds = Number(raw);
    if (totalSeconds <= 0) return null;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  if (raw.includes(":")) {
    const parts = raw.split(":").map((part) => Number(part));
    if (parts.some((part) => Number.isNaN(part))) return raw;
    if (parts.length === 3) {
      return `${parts[0]}:${String(parts[1]).padStart(2, "0")}:${String(parts[2]).padStart(2, "0")}`;
    }
    if (parts.length === 2) {
      return `${parts[0]}:${String(parts[1]).padStart(2, "0")}:00`;
    }
  }

  return raw;
};

const getCptTimeOnly = (details = {}) => {
  const time = pickDetailValue(
    details,
    "time",
    "rpmTime",
    "ccmTime",
    "duration",
    "totalTime",
    "timeSpent",
    "timespent",
    "minutes"
  );
  return formatDisplayTime(time);
};

const getCptTimeDisplayValue = (details = {}) => getCptTimeOnly(details) || DEFAULT_CPT_TIME;

const extractRpmTimeRaw = (details = {}) =>
  pickDetailValue(details, "rpmTime", "time", "duration", "totalTime", "timeSpent", "minutes");

const extractCcmTimeRaw = (details = {}) =>
  pickDetailValue(details, "ccmTime", "time", "duration", "totalTime", "timeSpent", "minutes");

/** Match PM Session Report time display (e.g. 0:24:50, 0:00:00). */
export const formatPmSessionRpmTime = (time) => {
  if (time === null || time === undefined || time === "") {
    return PM_SESSION_ZERO_TIME;
  }

  const raw = String(time).trim();
  if (!raw) {
    return PM_SESSION_ZERO_TIME;
  }

  if (/^\d+$/.test(raw)) {
    const totalSeconds = Number(raw);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  if (raw.includes(":")) {
    const parts = raw.split(":").map((part) => Number(part));
    if (!parts.some((part) => Number.isNaN(part))) {
      if (parts.length === 3) {
        return `${parts[0]}:${String(parts[1]).padStart(2, "0")}:${String(parts[2]).padStart(2, "0")}`;
      }
      if (parts.length === 2) {
        return `${parts[0]}:${String(parts[1]).padStart(2, "0")}:00`;
      }
    }
  }

  return raw;
};

const CptMetricDisplay = ({ details }) => (
  <span className="cpt-code-time">
    <i className="mdi mdi-clock-outline"></i>
    {getCptTimeDisplayValue(details)}
  </span>
);

const parseAdditionalDetailsRow = (rawCode, rawDetails = {}) => {
  const status = String(pickDetailValue(rawDetails, "status")).trim().toLowerCase();
  const timeValue = pickDetailValue(rawDetails, "rpmTime", "ccmTime", "time", "duration", "totalTime", "timeSpent");

  return {
    code: pickDetailValue(rawDetails, "code", "cptCode") || rawCode || "",
    name: pickDetailValue(rawDetails, "name", "title", "description"),
    time: timeValue,
    rpmTimeRaw: pickDetailValue(rawDetails, "rpmTime", "time", "duration", "totalTime", "timeSpent"),
    ccmTimeRaw: pickDetailValue(rawDetails, "ccmTime", "time", "duration", "totalTime", "timeSpent"),
    days: pickDetailValue(rawDetails, "days"),
    requiredDays: pickDetailValue(rawDetails, "requiredDays", "requireddays") || 16,
    status,
    isMet: status === "met" || pickDetailValue(rawDetails, "met") === true
  };
};

const collectAdditionalDetailRows = (details) => {
  const rows = [];

  if (!details || typeof details !== "object") {
    return rows;
  }

  if (Array.isArray(details)) {
    details.forEach((item) => {
      if (item && typeof item === "object") {
        rows.push(parseAdditionalDetailsRow(item.code || item.cptCode, item));
      }
    });
    return rows;
  }

  Object.entries(details).forEach(([key, value]) => {
    if (!/^\d{5}$/.test(String(key)) || !value) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item && typeof item === "object") {
          rows.push(parseAdditionalDetailsRow(key, item));
        }
      });
      return;
    }

    if (typeof value === "object") {
      rows.push(parseAdditionalDetailsRow(key, value));
    }
  });

  if (rows.length === 0) {
    rows.push(parseAdditionalDetailsRow("", details));
  }

  return rows;
};

const getCptEntryFromAdditional = (additionalDetails, code) => {
  if (!additionalDetails || typeof additionalDetails !== "object") return null;

  const normalizedCode = normalizeCptCodeKey(code);
  const direct = additionalDetails[normalizedCode];

  if (direct && typeof direct === "object") {
    return parseAdditionalDetailsRow(normalizedCode, direct);
  }

  return collectAdditionalDetailRows(additionalDetails).find(
    (row) => normalizeCptCodeKey(row.code) === normalizedCode
  ) || null;
};

const mergeCptCodeDetails = (code, details = {}, additionalDetails) => {
  const supplemental = getCptEntryFromAdditional(additionalDetails, code);
  const parsedDetails = details && typeof details === "object" ? details : {};
  const merged = {
    ...(supplemental || {}),
    ...parsedDetails,
    name:
      parsedDetails.name ||
      supplemental?.name ||
      pickDetailValue(parsedDetails, "title", "description") ||
      supplemental?.name,
    days: parsedDetails.days ?? supplemental?.days,
    requiredDays: parsedDetails.requiredDays ?? supplemental?.requiredDays ?? 16,
    met: parsedDetails.met !== undefined ? parsedDetails.met : supplemental?.isMet === true,
    isMet: parsedDetails.met === true || supplemental?.isMet === true
  };

  let timeValue = getCptTimeOnly(merged);
  if (!timeValue && additionalDetails) {
    timeValue = getCptTimeOnly(additionalDetails);
  }
  if (timeValue) {
    merged.time = timeValue;
  }

  return merged;
};

const mapCptCodeEntriesToRows = (cptCodes, additionalDetails) =>
  Object.entries(cptCodes || {})
    .map(([code, details]) => {
      if (!details || !/^\d{5}$/.test(String(code))) return null;
      const merged = mergeCptCodeDetails(code, details, additionalDetails);
      return {
        code: normalizeCptCodeKey(code),
        name: merged.name,
        time: getCptTimeOnly(merged) || "",
        rpmTimeRaw: extractRpmTimeRaw(merged),
        ccmTimeRaw: extractCcmTimeRaw(merged),
        days: merged.days,
        requiredDays: merged.requiredDays || 16,
        isMet: merged.met === true || merged.isMet === true
      };
    })
    .filter(Boolean)
    .filter((row) => row.code || row.time || row.days);

const collectPatientCptRows = (patient) => {
  const additionalDetails = patient?.additionalDetails;

  if (patient?.cptCodes && typeof patient.cptCodes === "object" && Object.keys(patient.cptCodes).length > 0) {
    return mapCptCodeEntriesToRows(patient.cptCodes, additionalDetails);
  }

  return collectAdditionalDetailRows(additionalDetails).filter(
    (row) => row.code || row.name || row.time || row.days || row.status
  );
};

export const shouldUseRpmClinicalLayout = (rows = []) => {
  const codeKeys = rows
    .map((row) => normalizeCptCodeKey(row.code))
    .filter((code) => /^\d{5}$/.test(code));

  if (codeKeys.length === 0) {
    return false;
  }

  const hasRpmClinical = codeKeys.some((code) => RPM_CLINICAL_CODES.has(code));
  const hasOtherClinicalCodes = codeKeys.some((code) => !RPM_CLINICAL_CODES.has(code));

  return hasRpmClinical && !hasOtherClinicalCodes;
};

const getClinicalRpmTimeFromRows = (rows = []) => {
  const rpmRows = rows.filter((row) => RPM_CLINICAL_CODES.has(normalizeCptCodeKey(row.code)));

  for (const row of rpmRows) {
    if (row.rpmTimeRaw !== undefined && row.rpmTimeRaw !== null && row.rpmTimeRaw !== "") {
      return formatPmSessionRpmTime(row.rpmTimeRaw);
    }
  }

  for (const row of rpmRows) {
    if (row.time) {
      return formatPmSessionRpmTime(row.time);
    }
  }

  return PM_SESSION_ZERO_TIME;
};

export const shouldUseCcmClinicalLayout = (rows = []) => {
  const codeKeys = rows
    .map((row) => normalizeCptCodeKey(row.code))
    .filter((code) => /^\d{5}$/.test(code));

  if (codeKeys.length === 0) {
    return false;
  }

  const hasCcmClinical = codeKeys.some((code) => CCM_CLINICAL_CODES.has(code));
  const hasOtherClinicalCodes = codeKeys.some((code) => !CCM_CLINICAL_CODES.has(code));

  return hasCcmClinical && !hasOtherClinicalCodes;
};

const getClinicalCcmTimeFromRows = (rows = []) => {
  const ccmRows = rows.filter((row) => CCM_CLINICAL_CODES.has(normalizeCptCodeKey(row.code)));

  for (const row of ccmRows) {
    if (row.ccmTimeRaw !== undefined && row.ccmTimeRaw !== null && row.ccmTimeRaw !== "") {
      return formatPmSessionRpmTime(row.ccmTimeRaw);
    }
  }

  for (const row of ccmRows) {
    if (row.time) {
      return formatPmSessionRpmTime(row.time);
    }
  }

  return PM_SESSION_ZERO_TIME;
};

/** CPT column: all CCM codes that qualified for this patient (PM Session billable pattern). */
const getQualifyingCcmClinicalMetCodes = (rows = []) => {
  const byCode = rows.reduce((acc, row) => {
    acc[normalizeCptCodeKey(row.code)] = row;
    return acc;
  }, {});

  const qualifying = [];
  const row99490 = byCode[CPT_CODE_99490];
  const row99439 = byCode[CPT_CODE_99439];
  const sharedCcmMinutes = parseTimeToMinutes(
    row99439?.ccmTimeRaw ||
      row99490?.ccmTimeRaw ||
      row99439?.time ||
      row99490?.time
  );

  if (row99490?.isMet) {
    qualifying.push({ code: CPT_CODE_99490, exportLabel: `${CPT_CODE_99490} Met` });
  }
  if (row99439?.isMet) {
    const displayCount = get99439DisplayCount(
      parseTimeToMinutes(row99439.ccmTimeRaw || row99439.time) || sharedCcmMinutes
    );
    for (let index = 0; index < displayCount; index += 1) {
      qualifying.push({ code: CPT_CODE_99439, exportLabel: `${CPT_CODE_99439} Met` });
    }
  }

  return qualifying;
};

const buildCcmClinicalPatientFields = (patient) => {
  const rows = collectPatientCptRows(patient);
  const clinicalCcmTime = getClinicalCcmTimeFromRows(rows);
  const qualifyingCodes = getQualifyingCcmClinicalMetCodes(rows);
  const billableCptCodesExport = qualifyingCodes.length > 0
    ? qualifyingCodes.map((item) => item.exportLabel).join(", ")
    : "";
  const billableCptCodesCsv = qualifyingCodes.length > 0
    ? qualifyingCodes.map((item) => item.code).join(", ")
    : "";

  return {
    clinicalCcmTimeDisplay: clinicalCcmTime,
    clinicalCcmTimeExport: clinicalCcmTime,
    billableCptCodesDisplay: billableCptCodesCsv || "-",
    billableCptCodesExport: billableCptCodesCsv,
    ccmClinicalSearchText: [clinicalCcmTime, billableCptCodesExport, billableCptCodesCsv].filter(Boolean).join(" ")
  };
};

export const ClinicalCcmTimeCell = ({ row }) => {
  const rows = collectPatientCptRows(row);
  if (!shouldUseCcmClinicalLayout(rows)) {
    return "-";
  }

  return (
    <div className="rpm-clinical-time-pm text-center">
      <div className="rpm-clinical-time-value mb-0">{getClinicalCcmTimeFromRows(rows)}</div>
      <small className="rpm-clinical-time-hint">
        <b>HOUR:MINUTE:SECOND</b>
      </small>
    </div>
  );
};

export const shouldUseTechnicalLayout = (rows = []) => {
  const codeKeys = rows
    .map((row) => normalizeCptCodeKey(row.code))
    .filter((code) => /^\d{5}$/.test(code));

  if (codeKeys.length === 0) {
    return false;
  }

  const hasTechnical = codeKeys.some((code) => TECHNICAL_CODES.has(code));
  const hasOtherCodes = codeKeys.some((code) => !TECHNICAL_CODES.has(code));

  return hasTechnical && !hasOtherCodes;
};

const getTechnicalAdherenceDaysFromPatient = (patient = {}, rows = []) => {
  const technicalRows = rows.filter((row) => TECHNICAL_CODES.has(normalizeCptCodeKey(row.code)));
  for (const row of technicalRows) {
    const days = Number(row.days);
    if (!Number.isNaN(days)) {
      return days;
    }
  }

  const patientDays = Number(patient?.adherenceDays ?? patient?.adherenceDaysValue);
  return Number.isNaN(patientDays) ? 0 : patientDays;
};

/** Billable technical CPT codes that qualified (PM Session pattern). */
const getQualifyingTechnicalMetCodes = (rows = []) => {
  const byCode = rows.reduce((acc, row) => {
    acc[normalizeCptCodeKey(row.code)] = row;
    return acc;
  }, {});

  const qualifying = [];
  const row99445 = byCode[CPT_CODE_99445];
  const row99454 = byCode[CPT_CODE_99454];

  if (row99445?.isMet) {
    qualifying.push({ code: CPT_CODE_99445, exportLabel: `${CPT_CODE_99445} Met` });
  }
  if (row99454?.isMet) {
    qualifying.push({ code: CPT_CODE_99454, exportLabel: `${CPT_CODE_99454} Met` });
  }

  return qualifying;
};

const buildTechnicalPatientFields = (patient) => {
  const rows = collectPatientCptRows(patient);
  const adherenceDays = getTechnicalAdherenceDaysFromPatient(patient, rows);
  const qualifyingCodes = getQualifyingTechnicalMetCodes(rows);
  const billableCptCodesExport = qualifyingCodes.length > 0
    ? qualifyingCodes.map((item) => item.exportLabel).join(", ")
    : "";
  const billableCptCodesCsv = qualifyingCodes.length > 0
    ? qualifyingCodes.map((item) => item.code).join(", ")
    : "";

  return {
    technicalAdherenceDaysDisplay: adherenceDays,
    technicalAdherenceDaysExport: adherenceDays,
    billableCptCodesDisplay: billableCptCodesCsv || "-",
    billableCptCodesExport: billableCptCodesCsv,
    technicalSearchText: [String(adherenceDays), billableCptCodesExport, billableCptCodesCsv].filter(Boolean).join(" ")
  };
};

export const TechnicalAdherenceDaysCell = ({ row }) => {
  const rows = collectPatientCptRows(row);
  if (!shouldUseTechnicalLayout(rows)) {
    return "-";
  }

  return (
    <div className="rpm-clinical-time-pm text-center">
      <div className="rpm-clinical-time-value mb-0">{getTechnicalAdherenceDaysFromPatient(row, rows)}</div>
      <small className="rpm-clinical-time-hint">
        <b>READING DAYS</b>
      </small>
    </div>
  );
};

const resolveQualifyingClinicalMetCodes = (rows = [], layout = "default") => {
  if (layout === CPT_DETAILS_LAYOUT_TECHNICAL || shouldUseTechnicalLayout(rows)) {
    return {
      qualifyingCodes: getQualifyingTechnicalMetCodes(rows),
      badgeClassMap: TECHNICAL_PM_BADGE_CLASS
    };
  }
  if (layout === CPT_DETAILS_LAYOUT_CCM_CLINICAL || shouldUseCcmClinicalLayout(rows)) {
    return {
      qualifyingCodes: getQualifyingCcmClinicalMetCodes(rows),
      badgeClassMap: CCM_CLINICAL_PM_BADGE_CLASS
    };
  }
  if (layout === CPT_DETAILS_LAYOUT_RPM_CLINICAL || shouldUseRpmClinicalLayout(rows)) {
    return {
      qualifyingCodes: getQualifyingRpmClinicalMetCodes(rows),
      badgeClassMap: RPM_CLINICAL_PM_BADGE_CLASS
    };
  }
  return null;
};

/** CPT column: all codes that qualified for this patient (PM Session billable pattern). */
const getQualifyingRpmClinicalMetCodes = (rows = []) => {
  const byCode = rows.reduce((acc, row) => {
    acc[normalizeCptCodeKey(row.code)] = row;
    return acc;
  }, {});

  const qualifying = [];
  const row99470 = byCode[CPT_CODE_99470];
  const row99457 = byCode[CPT_CODE_99457];
  const row99458 = byCode[CPT_CODE_99458];
  const sharedRpmMinutes = parseTimeToMinutes(
    row99458?.rpmTimeRaw ||
      row99457?.rpmTimeRaw ||
      row99470?.rpmTimeRaw ||
      row99458?.time ||
      row99457?.time ||
      row99470?.time
  );

  if (row99470?.isMet) {
    qualifying.push({ code: CPT_CODE_99470, exportLabel: `${CPT_CODE_99470} Met` });
  }
  if (row99457?.isMet) {
    qualifying.push({ code: CPT_CODE_99457, exportLabel: `${CPT_CODE_99457} Met` });
  }
  if (row99458?.isMet) {
    const displayCount = get99458DisplayCount(
      parseTimeToMinutes(row99458.rpmTimeRaw || row99458.time) || sharedRpmMinutes
    );
    for (let index = 0; index < displayCount; index += 1) {
      qualifying.push({ code: CPT_CODE_99458, exportLabel: `${CPT_CODE_99458} Met` });
    }
  }

  return qualifying;
};

const buildRpmClinicalPatientFields = (patient) => {
  const rows = collectPatientCptRows(patient);
  const clinicalRpmTime = getClinicalRpmTimeFromRows(rows);
  const qualifyingCodes = getQualifyingRpmClinicalMetCodes(rows);
  const billableCptCodesExport = qualifyingCodes.length > 0
    ? qualifyingCodes.map((item) => item.exportLabel).join(", ")
    : "";
  const billableCptCodesCsv = qualifyingCodes.length > 0
    ? qualifyingCodes.map((item) => item.code).join(", ")
    : "";

  return {
    clinicalRpmTimeDisplay: clinicalRpmTime,
    clinicalRpmTimeExport: clinicalRpmTime,
    billableCptCodesDisplay: billableCptCodesCsv || "-",
    billableCptCodesExport: billableCptCodesCsv,
    rpmClinicalSearchText: [clinicalRpmTime, billableCptCodesExport, billableCptCodesCsv].filter(Boolean).join(" ")
  };
};

export const ClinicalRpmTimeCell = ({ row }) => {
  const rows = collectPatientCptRows(row);
  if (!shouldUseRpmClinicalLayout(rows)) {
    return "-";
  }

  return (
    <div className="rpm-clinical-time-pm text-center">
      <div className="rpm-clinical-time-value mb-0">{getClinicalRpmTimeFromRows(rows)}</div>
      <small className="rpm-clinical-time-hint">
        <b>HOUR:MINUTE:SECOND</b>
      </small>
    </div>
  );
};

export const BillableCptCodesCell = ({ row, layout = "default" }) => {
  const rows = collectPatientCptRows(row);
  const resolved = resolveQualifyingClinicalMetCodes(rows, layout);
  if (!resolved) {
    return "-";
  }

  const { qualifyingCodes, badgeClassMap } = resolved;
  if (qualifyingCodes.length === 0) {
    return <div className="rpm-billable-cpt-codes-wrap text-center">-</div>;
  }

  return (
    <div className="rpm-billable-cpt-codes-wrap text-center">
      {qualifyingCodes.map((item, index) => (
        <span
          key={`${item.code}-${index}`}
          className={badgeClassMap[item.code] || "btn btn-sm btn-secondary session-code-button-radial rpm-billable-pm-code-badge"}
          title={item.exportLabel}
          role="img"
          aria-label={item.exportLabel}
        >
          {item.code}
        </span>
      ))}
    </div>
  );
};

const formatCptTime = (row) => getCptTimeDisplayValue(row);

const buildCptExportByCode = (patient, cptStatusFilter = null) => {
  const rows = collectPatientCptRows(patient).filter((row) =>
    matchesCptStatusFilter(row.isMet, cptStatusFilter)
  );
  const byCode = {};
  const otherRows = rows.filter((row) => normalizeCptCodeKey(row.code) !== CPT_CODE_99458);
  const rows99458 = rows.filter((row) => normalizeCptCodeKey(row.code) === CPT_CODE_99458);

  otherRows.forEach((row) => {
    const code = normalizeCptCodeKey(row.code);
    if (!code) return;
    byCode[code] = formatCptTime(row);
  });

  if (rows99458.length > 0) {
    const group99458 = get99458GroupState(rows99458);
    byCode[CPT_CODE_99458] = formatCptTime(group99458.template);
  }

  return byCode;
};

export const collectCptExportCodes = (patients = [], cptStatusFilter = null) => {
  const codes = new Set();

  patients.forEach((patient) => {
    Object.keys(buildCptExportByCode(patient, cptStatusFilter)).forEach((code) => codes.add(code));
  });

  return Array.from(codes).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

export const getCptExportColumns = (cptCodes = []) =>
  cptCodes.flatMap((code) => [
    { dataField: `cptExportCode_${code}`, text: `CPT Code - ${code}`, csvExport: true },
    { dataField: `cptExportTime_${code}`, text: `Time - ${code}`, csvExport: true }
  ]);

export const getRpmClinicalExportColumns = () => [
  { dataField: "clinicalRpmTimeExport", text: "Clinical RPM Time", csvExport: true },
  { dataField: "billableCptCodesExport", text: "Billable CPT Code(s)", csvExport: true }
];

export const getCcmClinicalExportColumns = () => [
  { dataField: "clinicalCcmTimeExport", text: "Clinical CCM Time", csvExport: true },
  { dataField: "billableCptCodesExport", text: "Billable CPT Code(s)", csvExport: true }
];

export const getTechnicalExportColumns = () => [
  { dataField: "technicalAdherenceDaysExport", text: "Adherence Days", csvExport: true },
  { dataField: "billableCptCodesExport", text: "Billable CPT Code(s)", csvExport: true }
];

export const getPmServiceExportColumns = (layout = CPT_DETAILS_LAYOUT_RPM_CLINICAL) => {
  if (layout === CPT_DETAILS_LAYOUT_TECHNICAL) {
    return getTechnicalExportColumns();
  }
  if (layout === CPT_DETAILS_LAYOUT_CCM_CLINICAL) {
    return getCcmClinicalExportColumns();
  }
  return getRpmClinicalExportColumns();
};

/** @deprecated Use getPmServiceExportColumns */
export const getClinicalPmExportColumns = getPmServiceExportColumns;

export const buildCptExportFields = (patient, cptExportCodes = [], cptStatusFilter = null) => {
  const byCode = buildCptExportByCode(patient, cptStatusFilter);

  return cptExportCodes.reduce((acc, code) => {
    acc[`cptExportCode_${code}`] = byCode[code] ? code : "-";
    acc[`cptExportTime_${code}`] = byCode[code] || DEFAULT_CPT_TIME;
    return acc;
  }, {});
};

export const ComponentCPTCell = ({ cptCodes, additionalDetails, cptStatusFilter, layout = "default" }) => {
  if (!cptCodes || typeof cptCodes !== 'object') {
    return "-";
  }
  
  const codes = Object.entries(cptCodes);
  if (codes.length === 0) {
    return "-";
  }

  const mergedRows = mapCptCodeEntriesToRows(cptCodes, additionalDetails);
  if (layout === CPT_DETAILS_LAYOUT_TECHNICAL || shouldUseTechnicalLayout(mergedRows)) {
    return (
      <div className="rpm-clinical-details-combined">
        <TechnicalAdherenceDaysCell row={{ cptCodes, additionalDetails }} />
        <BillableCptCodesCell row={{ cptCodes, additionalDetails }} layout={layout} />
      </div>
    );
  }

  if (layout === CPT_DETAILS_LAYOUT_CCM_CLINICAL || shouldUseCcmClinicalLayout(mergedRows)) {
    return (
      <div className="rpm-clinical-details-combined">
        <ClinicalCcmTimeCell row={{ cptCodes, additionalDetails }} />
        <BillableCptCodesCell row={{ cptCodes, additionalDetails }} layout={layout} />
      </div>
    );
  }

  if (layout === CPT_DETAILS_LAYOUT_RPM_CLINICAL || shouldUseRpmClinicalLayout(mergedRows)) {
    return (
      <div className="rpm-clinical-details-combined">
        <ClinicalRpmTimeCell row={{ cptCodes, additionalDetails }} />
        <BillableCptCodesCell row={{ cptCodes, additionalDetails }} layout={layout} />
      </div>
    );
  }
  
  const renderItems = [];
  codes.forEach(([code, details]) => {
    if (!details) return;

    const merged = mergeCptCodeDetails(code, details, additionalDetails);
    const isMet = merged.met === true || merged.isMet === true;
    if (!matchesCptStatusFilter(isMet, cptStatusFilter)) return;
    const codeLabel = merged.name || code;
    const timeOnly = getCptTimeOnly(merged);
    const normalizedCode = normalizeCptCodeKey(code);
    if (normalizedCode === CPT_CODE_99458) {
      const displayCount = get99458DisplayCount(parseTimeToMinutes(merged.time || timeOnly));
      renderItems.push(
        renderCpt99458Group({
          template: {
            name: codeLabel,
            time: merged.time || timeOnly,
            days: merged.days,
            requiredDays: merged.requiredDays || 16,
            isMet,
            status: isMet ? "met" : "not met"
          },
          displayCount
        })
      );
      return;
    }

    renderItems.push(
      <div key={code} className="cpt-code-item">
        <span className={`cpt-code-badge ${isMet ? "met" : "not-met"}`}>
          {code}
        </span>
        <span className="cpt-code-name">{codeLabel}</span>
        <CptMetricDisplay details={merged} />
      </div>
    );
  });

  if (renderItems.length === 0) {
    return "-";
  }

  return <div className="cpt-details-cell">{renderItems}</div>;
};

export const CPTCell = ({ details, cptStatusFilter, layout = "default" }) => {
  if (!details) {
    return "-";
  }

  const normalizeMap = (obj = {}) => Object.entries(obj).reduce((acc, [key, value]) => {
    const normalizedKey = String(key).toLowerCase().replace(/[\s_-]/g, "");
    acc[normalizedKey] = value;
    return acc;
  }, {});

  const parseRow = (rawCode, rawDetails = {}) => {
    const entry = normalizeMap(rawDetails);
    const pick = (...keys) => {
      for (const key of keys) {
        const value = entry[key.toLowerCase().replace(/[\s_-]/g, "")];
        if (value !== undefined && value !== null && value !== "") return value;
      }
      return "";
    };

    const status = String(pick("status")).trim().toLowerCase();
    return {
      code: pick("code", "cptCode") || rawCode || "",
      name: pick("name", "title", "description"),
      time: pick("rpmTime", "ccmTime", "time"),
      rpmTimeRaw: pick("rpmTime", "time"),
      ccmTimeRaw: pick("ccmTime", "time"),
      days: pick("days"),
      requiredDays: pick("requiredDays", "requireddays") || 16,
      status,
      isMet: status === "met" || pick("met") === true
    };
  };

  const collectDetailRows = (source = {}) => {
    const collected = [];

    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (item && typeof item === "object") {
          collected.push(parseRow(item.code || item.cptCode, item));
        }
      });
      return collected;
    }

    if (!source || typeof source !== "object") {
      return collected;
    }

    Object.entries(source).forEach(([key, value]) => {
      if (!/^\d{5}$/.test(String(key)) || !value) return;

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item && typeof item === "object") {
            collected.push(parseRow(key, item));
          }
        });
        return;
      }

      if (typeof value === "object") {
        collected.push(parseRow(key, value));
      }
    });

    return collected;
  };

  const nestedRows = collectDetailRows(details);
  const rows = nestedRows.length > 0 ? nestedRows : [parseRow("", details)];
  const validRows = rows
    .filter((row) => row.code || row.name || row.time || row.days || row.status)
    .filter((row) => matchesCptStatusFilter(row.isMet, cptStatusFilter));
  if (validRows.length === 0) return "-";

  if (layout === CPT_DETAILS_LAYOUT_TECHNICAL || shouldUseTechnicalLayout(validRows)) {
    return (
      <div className="rpm-clinical-details-combined">
        <TechnicalAdherenceDaysCell row={{ additionalDetails: details }} />
        <BillableCptCodesCell row={{ additionalDetails: details }} layout={layout} />
      </div>
    );
  }

  if (layout === CPT_DETAILS_LAYOUT_CCM_CLINICAL || shouldUseCcmClinicalLayout(validRows)) {
    return (
      <div className="rpm-clinical-details-combined">
        <ClinicalCcmTimeCell row={{ additionalDetails: details }} />
        <BillableCptCodesCell row={{ additionalDetails: details }} layout={layout} />
      </div>
    );
  }

  if (layout === CPT_DETAILS_LAYOUT_RPM_CLINICAL || shouldUseRpmClinicalLayout(validRows)) {
    const rpmRows = validRows.map((row) => ({
      ...row,
      rpmTimeRaw: row.rpmTimeRaw || row.time || pickDetailValue(row, "rpmTime", "time")
    }));

    return (
      <div className="rpm-clinical-details-combined">
        <div className="rpm-clinical-time-pm text-center">
          <div className="rpm-clinical-time-value mb-0">{getClinicalRpmTimeFromRows(rpmRows)}</div>
          <small className="rpm-clinical-time-hint">
            <b>HOUR:MINUTE:SECOND</b>
          </small>
        </div>
        <BillableCptCodesCell row={{ additionalDetails: details }} layout={layout} />
      </div>
    );
  }

  const otherRows = validRows.filter((row) => normalizeCptCodeKey(row.code) !== CPT_CODE_99458);
  const rows99458 = validRows.filter((row) => normalizeCptCodeKey(row.code) === CPT_CODE_99458);
  const group99458 = get99458GroupState(rows99458);

  return (
    <div className="cpt-details-cell">
      {otherRows.map((row, index) => (
          <div key={`${row.code || "cpt"}-${index}`} className="cpt-code-item">
            {row.code && (
              <span className={`cpt-code-badge ${row.isMet ? "met" : "not-met"}`}>
                {row.code}
              </span>
            )}
            <span className="cpt-code-name">{row.name || row.code || "-"}</span>
            <CptMetricDisplay
              details={{
                ...row,
                time: row.time || pickDetailValue(row, "rpmTime", "ccmTime", "time")
              }}
            />
          </div>
      ))}
      {group99458 &&
        renderCpt99458Group({
          template: group99458.template,
          displayCount: group99458.displayCount,
          showStatus: false
        })}
    </div>
  );
};

export const StatusCell = ({ status, label }) => (
  <span className={`patient-status-badge ${status === "met" ? "met" : "not-met"}`}>
    {label}
  </span>
);

export const CareProviderCell = ({ provider }) => (
  provider && provider !== "-" ? <span className="date-text-display">{formatName(provider)}</span> : "-"
);

export const PatientActionCell = ({ onViewPatient }) => (
  <div className="patient-action-cell-wrap">
    <button type="button" className="patient-details-btn" onClick={onViewPatient}>
      <i className="mdi mdi-file-document-outline"></i>
      <span>Details</span>
    </button>
  </div>
);

const buildCptDetailsColumns = ({ cptDetailsLayout = "default" } = {}) => {
  if (cptDetailsLayout === CPT_DETAILS_LAYOUT_TECHNICAL) {
    return [
      {
        dataField: "technicalAdherenceDaysDisplay",
        text: "Adherence Days",
        sort: true,
        sortValue: (cell) => Number(cell) || 0,
        formatter: (cell, row) => <TechnicalAdherenceDaysCell row={row} />,
        headerStyle: () => ({ minWidth: "130px", textAlign: "center" }),
        classes: "text-center",
        headerClasses: "text-center"
      },
      {
        dataField: "billableCptCodesDisplay",
        text: "Billable CPT Code(s)",
        sort: true,
        formatter: (cell, row) => (
          <BillableCptCodesCell row={row} layout={CPT_DETAILS_LAYOUT_TECHNICAL} />
        ),
        headerStyle: () => ({ minWidth: "150px", textAlign: "center" }),
        classes: "text-center",
        headerClasses: "text-center"
      }
    ];
  }

  if (cptDetailsLayout === CPT_DETAILS_LAYOUT_CCM_CLINICAL) {
    return [
      {
        dataField: "clinicalCcmTimeDisplay",
        text: "Clinical CCM Time",
        sort: true,
        sortValue: (cell) => cell || "",
        formatter: (cell, row) => <ClinicalCcmTimeCell row={row} />,
        headerStyle: () => ({ minWidth: "150px", textAlign: "center" }),
        classes: "text-center",
        headerClasses: "text-center"
      },
      {
        dataField: "billableCptCodesDisplay",
        text: "Billable CPT Code(s)",
        sort: true,
        formatter: (cell, row) => (
          <BillableCptCodesCell row={row} layout={CPT_DETAILS_LAYOUT_CCM_CLINICAL} />
        ),
        headerStyle: () => ({ minWidth: "150px", textAlign: "center" }),
        classes: "text-center",
        headerClasses: "text-center"
      }
    ];
  }

  if (cptDetailsLayout === CPT_DETAILS_LAYOUT_RPM_CLINICAL) {
    return [
      {
        dataField: "clinicalRpmTimeDisplay",
        text: "Clinical RPM Time",
        sort: true,
        sortValue: (cell) => cell || "",
        formatter: (cell, row) => <ClinicalRpmTimeCell row={row} />,
        headerStyle: () => ({ minWidth: "150px", textAlign: "center" }),
        classes: "text-center",
        headerClasses: "text-center"
      },
      {
        dataField: "billableCptCodesDisplay",
        text: "Billable CPT Code(s)",
        sort: true,
        formatter: (cell, row) => (
          <BillableCptCodesCell row={row} layout={CPT_DETAILS_LAYOUT_RPM_CLINICAL} />
        ),
        headerStyle: () => ({ minWidth: "150px", textAlign: "center" }),
        classes: "text-center",
        headerClasses: "text-center"
      }
    ];
  }

  return [
    {
      dataField: "additionalDetailsText",
      text: "CPT Code Details",
      sort: true,
      formatter: (cell, row) => {
        if (row?.cptCodes) {
          return <ComponentCPTCell cptCodes={row.cptCodes} additionalDetails={row.additionalDetails} />;
        }
        return <CPTCell details={row?.additionalDetails} />;
      },
      headerStyle: () => ({ minWidth: "220px" })
    }
  ];
};

export const getPatientListColumns = (
  { onViewPatientProfile, cptDetailsLayout = "default" } = {},
  additionalColumns = []
) => {
  const defaultColumns = [
    {
      dataField: "patientName",
      text: "Last, First Name",
      sort: true,
      formatter: (cell, row) => (
        <PatientNameCell
          name={cell}
          email={row?.email}
          phone={row?.phone}
          profileImage={row?.profileImage}
          isActive={row?.isActive}
          onViewPatient={() => onViewPatientProfile?.(row)}
        />
      ),
      headerStyle: () => ({ minWidth: "250px" })
    },
    {
      dataField: "enrolledDateFormatted",
      text: "Enrolled Date",
      sort: true,
      sortValue: (cell, row) => row?.enrolledDateValue || "",
      formatter: (cell) => <DateCell date={cell} iconClass="mdi-calendar-outline" />
    },
    {
      dataField: "lastUploadDateFormatted",
      text: "Last Uploaded Date",
      sort: true,
      sortValue: (cell, row) => row?.lastUploadDateValue || "",
      formatter: (cell) => <DateCell date={cell} iconClass="mdi-calendar-upload" />
    },
    {
      dataField: "mainCareProviderName",
      text: "Main Care Provider",
      sort: true,
      formatter: (cell) => <CareProviderCell provider={cell} />
    },
    {
      dataField: "organization",
      text: "Organization",
      sort: true,
      formatter: (cell) => <OrganizationCell organization={cell} />
    },
    {
      dataField: "insuranceProviderLabel",
      text: "Primary Insurance Provider",
      sort: true,
      formatter: (cell) => <InsuranceCell insurance={cell} />
    },
    {
      dataField: "enrolledProgramsLabel",
      text: "Enrolled Program",
      sort: true,
      formatter: (cell) => <ProgramCell programs={cell} />
    },
    ...(cptDetailsLayout !== CPT_DETAILS_LAYOUT_TECHNICAL
      ? [
          {
            dataField: "adherenceDaysValue",
            text: "Adherence Days",
            sort: true,
            formatter: (cell) => <AdherenceDaysCell days={cell} />
          }
        ]
      : []),
    ...buildCptDetailsColumns({ cptDetailsLayout }),
    {
      dataField: "statusLabel",
      text: "Status",
      sort: true,
      formatter: (cell, row) => <StatusCell status={row?.status} label={cell} />
    },
    {
      dataField: "patientActions",
      text: "Action",
      isDummyField: true,
      csvExport: false,
      formatter: (cell, row) => <PatientActionCell onViewPatient={() => onViewPatientProfile?.(row)} />,
      headerStyle: () => ({ minWidth: "130px", textAlign: "center" }),
      headerClasses: "text-center",
      classes: "text-center"
    }
  ];

  return [...defaultColumns, ...additionalColumns];
};

export const processPatientData = (
  patients = [],
  { cptExportCodes = [], cptStatusFilter = null, cptDetailsLayout = "default" } = {}
) => {
  return patients.map((patient) => {
    const byCode = buildCptExportByCode(patient, cptStatusFilter);
    const cptExportFields = buildCptExportFields(patient, cptExportCodes, cptStatusFilter);
    const cptCodes = Object.keys(byCode);
    const patientCptRows = collectPatientCptRows(patient);
    const useRpmClinicalLayout =
      cptDetailsLayout === CPT_DETAILS_LAYOUT_RPM_CLINICAL || shouldUseRpmClinicalLayout(patientCptRows);
    const useCcmClinicalLayout =
      cptDetailsLayout === CPT_DETAILS_LAYOUT_CCM_CLINICAL || shouldUseCcmClinicalLayout(patientCptRows);
    const useTechnicalLayout =
      cptDetailsLayout === CPT_DETAILS_LAYOUT_TECHNICAL || shouldUseTechnicalLayout(patientCptRows);
    const rpmClinicalFields = useRpmClinicalLayout ? buildRpmClinicalPatientFields(patient) : {};
    const ccmClinicalFields = useCcmClinicalLayout ? buildCcmClinicalPatientFields(patient) : {};
    const technicalFields = useTechnicalLayout ? buildTechnicalPatientFields(patient) : {};

    let additionalDetailsText = "-";
    if (useRpmClinicalLayout) {
      additionalDetailsText = rpmClinicalFields.rpmClinicalSearchText || "-";
    } else if (useCcmClinicalLayout) {
      additionalDetailsText = ccmClinicalFields.ccmClinicalSearchText || "-";
    } else if (useTechnicalLayout) {
      additionalDetailsText = technicalFields.technicalSearchText || "-";
    } else if (cptCodes.length > 0) {
      additionalDetailsText = cptCodes
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((code) => `${code}: Time: ${byCode[code]}`)
        .join(" | ");
    } else {
      const additionalDetails = patient?.additionalDetails || {};
      const legacyDetails = Object.keys(additionalDetails)
        .filter((key) => additionalDetails[key] !== undefined && additionalDetails[key] !== null && additionalDetails[key] !== "")
        .map((key) => `${key.replace(/([A-Z])/g, " $1").trim()}: ${additionalDetails[key]}`)
        .join(" | ");
      if (legacyDetails) {
        additionalDetailsText = legacyDetails;
      }
    }
    
    const profileImage = patient?.profileImage
      ? `${process.env.REACT_APP_VIDEO_CALL_REQUEST}/${patient.profileImage}`
      : userImage;

    const enrolledProgramsLabel = Array.isArray(patient?.enrolledPrograms)
      ? patient.enrolledPrograms.join(", ")
      : patient?.enrolledPrograms || "-";

    const insuranceProviderLabel = patient?.insuranceProvider || "-";
    const lastUploadDateValue = patient?.lastUploadDate || "";
    const lastUploadDateFormatted = lastUploadDateValue
      ? (moment(lastUploadDateValue).isValid() ? moment(lastUploadDateValue).format("MMM DD, YYYY") : lastUploadDateValue)
      : "-";
    const enrolledDateValue = patient?.enrolledDate || "";
    const enrolledDateFormatted = enrolledDateValue
      ? (moment(enrolledDateValue).isValid() ? moment(enrolledDateValue).format("MMM DD, YYYY") : enrolledDateValue)
      : "-";

    return {
      ...patient,
      patientName: formatName(patient?.patientName),
      profileImage,
      organization: patient?.client?.organization || "-",
      mainCareProviderName: formatName(patient?.mainCareProvider?.name),
      enrolledProgramsLabel: enrolledProgramsLabel === "-" ? "-" : enrolledProgramsLabel.split(",").map(p => capitalizeFirstLetter(p.trim())).join(", "),
      adherenceDaysValue: patient?.adherenceDays || 0,
      insuranceProviderLabel: insuranceProviderLabel === "-" ? "-" : capitalizeFirstLetter(insuranceProviderLabel),
      isCalledLabel: patient?.isCalled ? "Called" : "Not Called",
      isActive: Boolean(patient?.isActive),
      lastUploadDateValue,
      lastUploadDateFormatted,
      enrolledDateValue,
      enrolledDateFormatted,
      additionalDetailsText: additionalDetailsText || "-",
      ...rpmClinicalFields,
      ...ccmClinicalFields,
      ...technicalFields,
      ...cptExportFields,
      statusLabel: patient?.status === "met" ? "MET" : patient?.status === "notMet" ? "NOT MET" : (patient?.status || "-").toUpperCase()
    };
  });
};